import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { BadgeStateEnum } from "@/enums";
import { generateValidClassNameHandler } from "@/lib/utils";

const badgeVariants = cva(
    "inline-flex w-fit items-center gap-1 truncate rounded p-1 text-center text-xs font-medium transition-colors focus:outline-none md:px-3 md:py-1",
    {
        compoundVariants: [
            // Default State Variants
            {
                class: "bg-primary text-primary-foreground",
                state: BadgeStateEnum.DEFAULT,
                variant: "default",
            },
            {
                class: "border border-primary bg-transparent text-primary",
                state: BadgeStateEnum.DEFAULT,
                variant: "outline",
            },
            // Light State Variants
            {
                class: "bg-gray-200 text-primary",
                state: BadgeStateEnum.LIGHT,
                variant: "default",
            },
            {
                class: "border border-gray-200 bg-transparent text-primary",
                state: BadgeStateEnum.LIGHT,
                variant: "outline",
            },
            // Danger State Variants
            {
                class: "bg-danger-bg text-danger",
                state: BadgeStateEnum.DANGER,
                variant: "default",
            },
            {
                class: "border border-danger bg-transparent text-danger",
                state: BadgeStateEnum.DANGER,
                variant: "outline",
            },
            // Warning State Variants
            {
                class: "bg-warning-bg text-warning",
                state: BadgeStateEnum.WARNING,
                variant: "default",
            },
            {
                class: "border border-warning bg-transparent text-warning",
                state: BadgeStateEnum.WARNING,
                variant: "outline",
            },
            // In Progress State Variants
            {
                class: "bg-primary-foreground text-chart-13",
                state: BadgeStateEnum.INPROGRESS,
                variant: "default",
            },
            {
                class: "border border-chart-13 bg-transparent text-chart-13",
                state: BadgeStateEnum.INPROGRESS,
                variant: "outline",
            },
            // Success State Variants
            {
                class: "bg-success-bg text-success",
                state: BadgeStateEnum.SUCCESS,
                variant: "default",
            },
            {
                class: "border border-success bg-transparent text-success",
                state: BadgeStateEnum.SUCCESS,
                variant: "outline",
            },
            // Completed Partially State Variants
            {
                class: "bg-chart-14 text-foreground",
                state: BadgeStateEnum.COMPLETED_PARTIALLY,
                variant: "default",
            },
            {
                class: "border border-foreground bg-transparent text-foreground",
                state: BadgeStateEnum.COMPLETED_PARTIALLY,
                variant: "outline",
            },
            // Not Processed State Variants
            {
                class: "bg-gray-200 text-foreground",
                state: BadgeStateEnum.NOT_PROCESSED,
                variant: "default",
            },
            {
                class: "border border-foreground bg-transparent text-foreground",
                state: BadgeStateEnum.NOT_PROCESSED,
                variant: "outline",
            },
        ],
        defaultVariants: {
            state: "default",
            variant: "default",
        },
        variants: {
            state: {
                completedPartially: "",
                danger: "",
                default: "",
                inprogress: "",
                light: "",
                notProcessed: "",
                success: "",
                warning: "",
            },
            variant: {
                default: "",
                outline: "",
            },
        },
    },
);

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {}

const Badge = ({
    className,
    state,
    variant,
    ...props
} : BadgeProps) => (
    <div
        className={generateValidClassNameHandler(
            badgeVariants({
                state,
                variant,
            }),
            className,
            "rounded",
        )}
        {...props}
    />
);

export {
    Badge,
    badgeVariants,
};
